import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { useTheme } from '../context/ThemeContext';

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="card px-3 py-2 text-xs shadow-lg">
      <p className="text-ink-light-muted dark:text-ink-muted mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }} className="font-semibold">
          {p.name}: {p.value} {p.dataKey === 'weight_kg' ? 'кг' : 'см'}
        </p>
      ))}
    </div>
  );
}

/**
 * @param {Array}  data     - array of measurement objects
 * @param {string} field    - which field to plot
 * @param {string} label    - display name
 * @param {string} color    - hex color
 */
export default function WeightChart({ data = [], field = 'weight_kg', label = 'Вес', color = '#E8006F' }) {
  const { isDark } = useTheme();
  const gridColor  = isDark ? '#2D2540' : '#E8D9F0';
  const textColor  = isDark ? '#9E8CB8' : '#7B5FA0';

  const chartData = [...data]
    .filter(m => m[field] != null)
    .sort((a, b) => new Date(a.measured_at) - new Date(b.measured_at))
    .map(m => ({
      date: new Date(m.measured_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }),
      [field]: parseFloat(m[field]),
    }));

  if (chartData.length < 2) {
    return (
      <div className="card p-4 flex items-center justify-center h-40 text-sm text-ink-light-muted dark:text-ink-muted">
        Добавь минимум 2 замера для отображения графика
      </div>
    );
  }

  const values = chartData.map(d => d[field]);
  const min    = Math.min(...values);
  const max    = Math.max(...values);
  const pad    = (max - min) * 0.15 || 1;

  return (
    <div className="card p-4">
      <p className="section-label mb-3">{label} - динамика</p>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis dataKey="date" tick={{ fill: textColor, fontSize: 11 }} tickLine={false} axisLine={false} />
          <YAxis
            domain={[min - pad, max + pad]}
            tick={{ fill: textColor, fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            tickFormatter={v => v.toFixed(1)}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey={field}
            name={label}
            stroke={color}
            strokeWidth={2.5}
            dot={{ r: 4, fill: color, strokeWidth: 0 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
